import {
  ShoppingCart,
  Package,
} from 'lucide-react';
import { cn } from '@/lib/classnames';
import type { RecentPurchase } from '../../../types';

interface PurchasesTabProps {readonly recentPurchases?: RecentPurchase[];}

export function PurchasesTab({ recentPurchases }: PurchasesTabProps) {
  if (!recentPurchases?.length) {
    return (
      <EmptyState
        icon={ShoppingCart}
        title="No purchase history"
        description="Recent purchase orders for this item will appear here."
      />
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="font-medium text-gray-900">Recent Purchase Orders</h3>
      <div className="overflow-hidden rounded-lg border">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PO #</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Supplier</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {recentPurchases.map((po) => (
              <PurchaseRow key={po.poId} purchase={po} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface PurchaseRowProps {readonly purchase: RecentPurchase;}

function PurchaseRow({ purchase }: PurchaseRowProps) {
  const getStatusStyle = (status: string) => {
    if (status === 'delivered') return 'bg-green-100 text-green-800';
    if (status === 'shipped') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm font-medium text-blue-600 cursor-pointer hover:underline">
        {purchase.poNumber}
      </td>
      <td className="px-4 py-3 text-sm text-gray-900">{purchase.supplierName}</td>
      <td className="px-4 py-3 text-sm text-gray-900 text-right">{purchase.quantity}</td>
      <td className="px-4 py-3 text-sm text-gray-900 text-right">${purchase.unitPrice.toFixed(2)}</td>
      <td className="px-4 py-3 text-sm font-medium text-gray-900 text-right">${purchase.totalPrice.toFixed(2)}</td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {new Date(purchase.orderDate).toLocaleDateString()}
      </td>
      <td className="px-4 py-3">
        <span className={cn(
          'inline-flex rounded-full px-2 py-1 text-xs font-medium',
          getStatusStyle(purchase.status)
        )}>
          {purchase.status}
        </span>
      </td>
    </tr>
  );
}

interface EmptyStateProps {
  readonly icon: typeof Package;
  readonly title: string;
  readonly description: string;
}

function EmptyState({
  icon: emptyIcon,
  title,
  description,
}: EmptyStateProps) {
  const IconComponent = emptyIcon;
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      <div className="rounded-full bg-gray-100 p-4 mb-4">
        <IconComponent className="h-8 w-8 text-gray-400" />
      </div>
      <h3 className="font-medium text-gray-900">{title}</h3>
      <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
    </div>
  );
}
